import { Button, Chip, TableRow, Tooltip } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ItemType, UploadType } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import { selectUpdate } from 'store/dataUpload/dataUploadSlice';
import { DataUploadCheckStatus } from '../DataUpload.helpers';
import IgnoreUploadModal from './IgnoreUploadModal';
import MergeConflictModal from './MergeConflictModal';
import RestoreUploadModal from './RestoreUploadModal';
import * as Styles from './UpdateDeviceListItem.styles';

interface UpdateDeviceListItemParams {
  item: ItemType;
  uploadType: UploadType;
  checkStatus: DataUploadCheckStatus;
  handleIgnoreItem: Function;
  handleRestoreItem: Function;
  handleApproveDuplicate: Function;
  handleIgnoreDuplicate: Function;
}

const UpdateDeviceListItem: FC<UpdateDeviceListItemParams> = ({
  item,
  uploadType,
  checkStatus,
  handleIgnoreItem,
  handleRestoreItem,
  handleApproveDuplicate,
  handleIgnoreDuplicate,
}) => {
  const [row, setRow] = useState<any[]>([]);
  const [modalIsOpen, setOpenSetModal] = useState(false);
  const [ignoreUploadModalOpen, setIgnoreUploadModalOpen] = useState(false);
  const [restoreUploadModalOpen, setRestoreUploadModalOpen] = useState(false);
  const updateArray = useSelector(selectUpdate);
  const [language] = useLanguage();

  const handleViewDataButtonClick = () => {
    setOpenSetModal(true);
  };

  const handleIgnoreUpload = (id: string | undefined) => {
    setIgnoreUploadModalOpen(true);
  };

  const handleRestoreButtonClick = () => {
    setRestoreUploadModalOpen(true);
  };

  const itemHasBeenUpdated = (array1: ItemType[], id: string) => {
    const item = array1.find((element: ItemType) => element.id === id);
    return item ? item.updated || false : false;
  };

  // ******** DEVICES **************************

  const uniqueDeviceRow = [
    item.device_id,
    item.description,
    item.num_parts,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={() => handleIgnoreUpload(item.device_id)}>
      {language.DataUpload.updateDeviceListItem.ignoreUpload}
    </Button>,
  ];

  const ignoreDeviceRow = [
    item.device_id,
    item.description,
    item.num_parts,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.reason ?? ''}>
        <div>{item.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.uniqueItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip
          placement="top"
          title="You can only restore items that have been manually ignored by the user. The data for this item is not able to be merged."
        >
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const ignoreUpdateDeviceRow = [
    item.updated_data?.device_id,
    item.updated_data?.description,
    item.updated_data?.num_parts,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.updated_data?.reason ?? ''}>
        <div>{item.updated_data?.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.updateItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip
          placement="top"
          title="You can only restore items that have been manually ignored by the user. The data for this item is not able to be merged."
        >
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const updateDeviceRow = [
    item.updated_data?.device_id,
    item.updated_data?.description,
    item.updated_data?.num_parts,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={handleViewDataButtonClick}>
      {language.DataUpload.updateDeviceListItem.updateDeviceRow}
    </Button>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.id ? (
        itemHasBeenUpdated(updateArray, item.id) ? (
          <Chip variant="outlined" label="Yes" color="success" />
        ) : (
          <div></div>
        )
      ) : (
        <></>
      )}
    </div>,
  ];

  // ****************************************

  // ******** SETS **************************

  const updateSetRow = [
    item.updated_data?.set_code,
    item.updated_data?.description,
    item.updated_data?.num_trays,
    item.updated_data?.num_devices,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={handleViewDataButtonClick}>
      {language.DataUpload.updateDeviceListItem.updateSetRow}
    </Button>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.id ? (
        itemHasBeenUpdated(updateArray, item.id) ? (
          <Chip variant="outlined" label="Yes" color="success" />
        ) : (
          <div></div>
        )
      ) : (
        <></>
      )}
    </div>,
  ];

  const uniqueSetRow = [
    item.set_code,
    item.description,
    item.num_trays,
    item.num_devices,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={() => handleIgnoreUpload(item.device_id)}>
      {language.DataUpload.updateDeviceListItem.ignoreUpload}
    </Button>,
  ];

  const ignoreSetRow = [
    item.set_code,
    item.description,
    item.num_trays,
    item.num_devices,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.reason ?? ''}>
        <div>{item.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.uniqueItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const ignoreUpdateSetRow = [
    item.updated_data?.set_code,
    item.updated_data?.description,
    item.updated_data?.num_trays,
    item.updated_data?.num_devices,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.updated_data?.reason ?? ''}>
        <div>{item.updated_data?.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.updateItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  // ****************************************

  // ******** SET SERIAL NUMBERS **************

  const updateSetSerialRow = [
    item.updated_data?.set_code,
    item.updated_data?.set_name,
    item.updated_data?.set_uss,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={handleViewDataButtonClick}>
      {language.DataUpload.updateDeviceListItem.updateSetSerialRow}
    </Button>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.id ? (
        itemHasBeenUpdated(updateArray, item.id) ? (
          <Chip variant="outlined" label="Yes" color="success" />
        ) : (
          <div></div>
        )
      ) : (
        <></>
      )}
    </div>,
  ];

  const uniqueSetSerialRow = [
    item.set_code,
    item.set_name,
    item.set_uss,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={() => handleIgnoreUpload(item.device_id)}>
      {language.DataUpload.updateDeviceListItem.ignoreUpload}
    </Button>,
  ];

  const ignoreSetSerialRow = [
    item.set_code,
    item.set_name,
    item.set_uss,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.reason ?? ''}>
        <div>{item.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.uniqueItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const ignoreUpdateSetSerialRow = [
    item.set_code,
    item.updated_data?.set_name,
    item.updated_data?.set_uss,
    // eslint-disable-next-line react/jsx-key
    <div>
      <Tooltip placement="top" title={item.updated_data?.reason ?? ''}>
        <div>{item.updated_data?.reason}</div>
      </Tooltip>
    </div>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.updateItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  // ****************************************

  // ******** PART ATTACHMENTS **************

  const uniquePartAttachmentRow = [
    item.device_id,
    item.file_path,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={() => handleIgnoreUpload(item.file_path)}>
      {language.DataUpload.updateDeviceListItem.ignoreUpload}
    </Button>,
  ];

  const ignorePartAttachmentRow = [
    item.device_id,
    item.file_path,
    item.reason,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.uniqueItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const ignoreUpdatePartAttachmentRow = [
    item.device_id,
    item.file_path,
    item.reason,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.updateItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const updatePartAttachmentRow = [
    item.device_id,
    item.file_path,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={handleViewDataButtonClick}>
      {language.DataUpload.updateDeviceListItem.updateSetRow}
    </Button>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.id ? (
        itemHasBeenUpdated(updateArray, item.id) ? (
          <Chip variant="outlined" label="Yes" color="success" />
        ) : (
          <div></div>
        )
      ) : (
        <></>
      )}
    </div>,
  ];

  // ****************************************

  // ******** SET ATTACHMENTS *****************

  const uniqueSetAttachmentRow = [
    item.display_code,
    item.file_path,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={() => handleIgnoreUpload(item.file_path)}>
      {language.DataUpload.updateDeviceListItem.ignoreUpload}
    </Button>,
  ];

  const ignoreSetAttachmentRow = [
    item.device_id,
    item.file_path,
    item.reason,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.uniqueItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const ignoreUpdateSetAttachmentRow = [
    item.device_id,
    item.file_path,
    item.reason,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.updateItem ? (
        <Button color="primary" variant="outlined" onClick={handleRestoreButtonClick}>
          {language.DataUpload.updateDeviceListItem.restore}
        </Button>
      ) : (
        <Tooltip placement="top" title={language.DataUpload.updateDeviceListItem.tooltip}>
          <div>{language.DataUpload.updateDeviceListItem.na}</div>
        </Tooltip>
      )}
    </div>,
  ];

  const updateSetAttachmentRow = [
    item.display_code,
    item.file_path,
    // eslint-disable-next-line react/jsx-key
    <Button color="primary" variant="outlined" onClick={handleViewDataButtonClick}>
      {language.DataUpload.updateDeviceListItem.updateSetRow}
    </Button>,
    // eslint-disable-next-line react/jsx-key
    <div>
      {item.id ? (
        itemHasBeenUpdated(updateArray, item.id) ? (
          <Chip variant="outlined" label="Yes" color="success" />
        ) : (
          <div></div>
        )
      ) : (
        <></>
      )}
    </div>,
  ];

  useEffect(() => {
    if (uploadType === UploadType['Device Upload']) {
      if (checkStatus === DataUploadCheckStatus.UNIQUE) {
        setRow(uniqueDeviceRow);
      } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
        item.updateItem ? setRow(ignoreUpdateDeviceRow) : setRow(ignoreDeviceRow);
      } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
        setRow(updateDeviceRow);
      }
    }

    if (uploadType === UploadType['Set Upload']) {
      if (checkStatus === DataUploadCheckStatus.UNIQUE) {
        setRow(uniqueSetRow);
      } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
        item.updateItem ? setRow(ignoreUpdateSetRow) : setRow(ignoreSetRow);
      } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
        setRow(updateSetRow);
      }
    }

    if (uploadType === UploadType['Set Serial Numbers']) {
      if (checkStatus === DataUploadCheckStatus.UNIQUE) {
        setRow(uniqueSetSerialRow);
      } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
        item.updateItem ? setRow(ignoreUpdateSetSerialRow) : setRow(ignoreSetSerialRow);
      } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
        setRow(updateSetSerialRow);
      }
    }

    if (uploadType === UploadType['Device Attachments']) {
      if (checkStatus === DataUploadCheckStatus.UNIQUE) {
        setRow(uniquePartAttachmentRow);
      } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
        item.updateItem ? setRow(ignoreUpdatePartAttachmentRow) : setRow(ignorePartAttachmentRow);
      } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
        setRow(updatePartAttachmentRow);
      }
    }

    if (uploadType === UploadType['Set Attachments']) {
      if (checkStatus === DataUploadCheckStatus.UNIQUE) {
        setRow(uniqueSetAttachmentRow);
      } else if (checkStatus === DataUploadCheckStatus.IGNORE) {
        item.updateItem ? setRow(ignoreUpdateSetAttachmentRow) : setRow(ignoreSetAttachmentRow);
      } else if (checkStatus === DataUploadCheckStatus.UPDATE) {
        setRow(updateSetAttachmentRow);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateArray]);

  return (
    <>
      <TableRow key={item.id} hover>
        {row?.map((cellData: any, index: number) => (
          <Styles.Cell key={index}>
            <Styles.TableCellDiv className="tableCell">{cellData}</Styles.TableCellDiv>
          </Styles.Cell>
        ))}
      </TableRow>
      <MergeConflictModal
        handleApproveDuplicate={handleApproveDuplicate}
        handleIgnoreDuplicate={handleIgnoreDuplicate}
        open={modalIsOpen}
        close={() => setOpenSetModal(false)}
        draft={item}
        uploadType={uploadType}
      />
      <IgnoreUploadModal
        open={ignoreUploadModalOpen}
        close={() => setIgnoreUploadModalOpen(false)}
        draft={item}
        uploadType={uploadType}
        handleIgnoreItem={handleIgnoreItem}
      />
      <RestoreUploadModal
        handleRestoreItem={handleRestoreItem}
        open={restoreUploadModalOpen}
        close={() => setRestoreUploadModalOpen(false)}
        draft={item}
      />
    </>
  );
};

export default UpdateDeviceListItem;
